import React, { useRef, useEffect, useState } from 'react';
import { ICON_HTML } from './components/icons';
import MainPage from './components/MainPage';
import OverlaysMap from './components/OverlaysMap';
import { SspContextProvider, TranslationProvider } from './did-utility';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import Label from './did-utility/src/Components/Label';
import { getSetup } from './did-utility/src/Functions/getSetup';
import addParam from './did-utility/src/Functions/addParam';
import {
	logSession,
	STORAGE_KEY,
} from './did-utility/src/Functions/ActivityLogger/util';
import { OfflineLamp } from './did-utility/src/Components/OfflineLamp';
import * as packageJson from '../package.json';
import useServiceWorker from './did-utility/src/Functions/useServiceWorker';
import Blackout from './components/Overlay/OverlayContent/Blackout';
import runtimeconfig from './../src/assets/runtimeconfig.json';
import { UseTimes } from './types';
import { ensureSetup } from './did-utility/src/Functions/ensureSetup';
import {
	sendHeartbeat,
	sendVitalsHeartbeat,
} from './did-utility/src/Functions/vitalsHeartbeatHelpers';

declare module 'i18next' {
	interface CustomTypeOptions {
		returnNull: false;
	}
}

ensureSetup();
localStorage.setItem('version', packageJson.version);

i18next.use(initReactI18next).init({
	resources: {},
	lng: 'en-US',
	fallbackLng: 'en-US',
	returnNull: false,
	interpolation: { escapeValue: false },
	saveMissing:
		(import.meta.env.VITE_REACT_APP_CHECK_KEYS as string) === 'true',
	missingKeyHandler: (lng, ns, key, fallbackValue) => {
		console.warn(lng, key, fallbackValue);
	},
});

let touchTimer: number | undefined;

function App() {
	const setup = getSetup();
	const projectAudi = '3210b23352780d9c98b8d23aef7493d3';
	const projectAll = '515fa3c0991f95f7cfbc6cf76f5837ad';
	const projectSetup = '119c3a6b0142615995dac6bee43c8971';
	const params = new URLSearchParams(window.location.search);
	const showLabel = params.get('label') === 'true' || setup?.label;
	const apk = params.get('apk') === 'true';
	const setupLink = window.location.href.replace('audi/', '');

	useServiceWorker();

	// Heartbeat (15 min) & Vitals-Heartbeat (1 min)
	useEffect(() => {
		if (!setup || !navigator.onLine) return;

		sendHeartbeat();
		sendVitalsHeartbeat();

		const hb = setInterval(sendHeartbeat, 15 * 60_000);
		const vit = setInterval(sendVitalsHeartbeat, 60_000);

		return () => {
			clearInterval(hb);
			clearInterval(vit);
		};
	}, []);

	const deviceType = params.get('device-type');
	const language = setup?.language || 'de-DE';
	const clientId = setup?.clientId || 'unknown';

	const accountContent = [
		'umdasch_version=' + (deviceType || 'unknown'),
		'secondscreen_rotation=' + localStorage.getItem('orientation') ||
			'left',
		'language=' + language,
		'client_id=' + clientId,
	].join('\n');

	useEffect(() => {
		if (params.get('apk') === 'true') {
			addParam('account', accountContent, true);
		}
	}, []);

	const resetTimer = () => {
		if (touchTimer) {
			window.clearTimeout(touchTimer);
		}
		if (!sessionStorage.getItem(STORAGE_KEY)) {
			logSession();
		}
		touchTimer = window.setTimeout(() => {
			window.clearTimeout(touchTimer);
			touchTimer = undefined;
			logSession(Date.now() - 3 * 60000);
			window.dispatchEvent(new CustomEvent('reset'));
			sessionStorage.removeItem(STORAGE_KEY);
		}, 3 * 60000);
	};

	useEffect(() => {
		window.addEventListener('click', resetTimer);

		window.addEventListener('pause-timer', () => {
			if (touchTimer) window.clearTimeout(touchTimer);
		});

		window.addEventListener('restart-timer', resetTimer);

		return () => {
			window.removeEventListener('click', resetTimer);

			window.removeEventListener('pause-timer', () => {
				if (touchTimer) window.clearTimeout(touchTimer);
			});

			window.removeEventListener('restart-timer', resetTimer);
		};
	}, []);

	if (params.get('apk') === 'true' && params.get('locked') === null) {
		params.set('locked', 'true');
		window.history.replaceState(null, '', '?' + params);
	}

	params.set('redirect', 'false');
	const chromebox =
		(window.location.href.includes('chrome') ||
			localStorage.getItem('hardware') === 'Chromebox') &&
		(params.get('label') === 'false' || params.get('label') === null);
	const url = new URL(`${window.location.origin}/audi/index.html`);
	const popupRef = useRef<Window | null>(null);
	console.log('chromebox:', chromebox);
	const openWindow = async () => {
		console.log('in open window');
		try {
			console.log('in try');
			if (localStorage.getItem('show-second-screen') === 'true') {
				console.log('in show second screen');
				if ('getScreenDetails' in window) {
					console.log('in getScreenDetails');
					const screens = await (window as any).getScreenDetails();

					if (screens.screens.length > 1) {
						console.log('in if screens length');
						const secondScreen = screens.screens[1];
						const width = secondScreen.width;
						const height = secondScreen.height;
						const left = secondScreen.left;
						const top = secondScreen.top;

						popupRef.current = window.open(
							url + '?label=true&redirect=false',
							'label',
							`popup=yes,width=${width},height=${height},left=${left},top=${top}`
						);
					} else {
						alert('Only one screen detected.');
					}
				} else {
					alert('Window Management API not supported.');
				}
			}
		} catch (error) {
			console.error('Error using Window Management API:', error);
		}
	};

	chromebox && openWindow();

	const redirect = () => {
		if (popupRef.current) {
			if ('location' in popupRef.current) {
				popupRef.current.location.href = `${window.location.origin}/index.html?label=true&redirect=false`;
			}
			if ('location' in popupRef.current) {
				popupRef.current.location.replace(
					`${window.location.origin}/index.html?label=true&redirect=false`
				);
			}
		}
	};

	window.addEventListener('redirect-label', redirect);

	const [blackout, setBlackout] = useState(false);
	const useTimes: UseTimes = runtimeconfig;

	const getCurrentDaySchedule = () => {
		const now = new Date();
		const day = now.getDay();
		return useTimes ? useTimes[day.toString()] : [];
	};

	const checkTimeAgainstSchedule = () => {
		const now = new Date();
		const daySchedule = getCurrentDaySchedule();

		return daySchedule.some((timeBlock: any) => {
			const { Start, End, Type } = timeBlock;
			if (Type === 'Timed') {
				const [startHour, startMinute] = Start.split(':').map(Number);
				const [endHour, endMinute] = End.split(':').map(Number);

				const startTime = new Date(now);
				startTime.setHours(startHour, startMinute, 0);

				const endTime = new Date(now);
				endTime.setHours(endHour, endMinute, 0);

				const currentTime = new Date(now);
				return currentTime >= startTime && currentTime <= endTime;
			}
			return false;
		});
	};

	useEffect(() => {
		if (setup.market.name === 'LUX') {
			const checkTime = () => {
				if (checkTimeAgainstSchedule()) {
					setBlackout(false);
					localStorage.removeItem('blackoutOverride');
				} else {
					setBlackout(true);
				}
			};

			checkTime();
			const interval = setInterval(checkTime, 60 * 1000);
			return () => clearInterval(interval);
		}
	}, []);

	const handleBlackoutOverride = () => {
		localStorage.setItem('blackoutOverride', 'true');
		window.dispatchEvent(new Event('blackoutOverride'));
		setBlackout(false);
	};

	useEffect(() => {
		const syncBlackoutState = (event: StorageEvent) => {
			if (event.key === 'blackoutOverride') {
				setBlackout(false);
			}
		};

		window.addEventListener('storage', syncBlackoutState);
		window.addEventListener('blackoutOverride', () => setBlackout(false));

		return () => {
			window.removeEventListener('storage', syncBlackoutState);
			window.removeEventListener('blackoutOverride', () =>
				setBlackout(false)
			);
		};
	}, []);

	return (
		<SspContextProvider
			url={import.meta.env.VITE_REACT_APP_SSP_BASE_URL as string}
			vin={
				new URLSearchParams(window.location.search).get('vin') as string
			}
			setupLink={setupLink}
			prepareOfflineUse={!apk || (apk && !showLabel)}
			showLabel={showLabel}
		>
			<TranslationProvider
				setupLink={setupLink}
				projectBrand={projectAudi}
				projectAll={projectAll}
				projectSetup={projectSetup}
			>
				<div
					className='svgtemplates'
					dangerouslySetInnerHTML={{ __html: ICON_HTML }}
				/>
				{!showLabel && (
					<div
						className={`App${
							params.get('prefers-reduced-motion') === 'true' ||
							apk
								? ' prefers-reduced-motion'
								: ''
						}`}
						onTouchStart={resetTimer}
						onClick={() => {
							handleBlackoutOverride();
						}}
					>
						{blackout &&
							localStorage.getItem('blackoutOverride') !==
								'true' && <Blackout />}
						<MainPage />
						<OverlaysMap />
					</div>
				)}
				{showLabel && (
					<Label
						brand={'audi'}
						apk={apk}
						blackout={
							blackout &&
							localStorage.getItem('blackoutOverride') !== 'true'
						}
					/>
				)}
			</TranslationProvider>
			{!showLabel && <OfflineLamp />}
		</SspContextProvider>
	);
}

export default App;
